import {useCallback, useEffect, useMemo, useState} from "react";
import { useDispatch, useSelector } from "react-redux";

import { AppState } from "store";
import {Input, Table, TablePagination} from "components/base";
import {parseBatteryDetailTable, parseBatteryHeartbeatTable, parseBatteryTable} from "utils/parsers";
import { TableProps } from "antd/lib/table";
import {Breadcrumb, Button, Card, Typography} from "antd";
import {KeyValue} from "../../../types";
import { backIcon, breadcrumbIcon, settingsIcon2 } from "svgs";
import {useHistory, useParams} from "react-router-dom";
import {TabNavs} from "../../index";
import {getBattery, getBatteryHeartbeats, setBatteryHBSelectedPage, setTripsSelectedPage} from "../../../store/actions";
import {BATTERY_STATUS} from "../../../utils/mapped-data";
import "styles/components/tab-content/battery-content/battery-details.scss";

const { Text } = Typography;

// Mobile-optimized columns - show only essential data on small screens
const getMobileColumns = (): TableProps<any>["columns"] => [
    {
        title: "Time",
        dataIndex: "created_at",
        width: 100,
        ellipsis: true,
    },
    {
        title: "SoC",
        dataIndex: "soc",
        width: 60,
        ellipsis: true,
    },
    {
        title: "Voltage",
        dataIndex: "voltage",
        width: 80,
        ellipsis: true,
    },
    {
        title: "Temp",
        dataIndex: "temperature",
        width: 70,
        ellipsis: true,
    },
];

// Tablet columns - show more data
const getTabletColumns = (): TableProps<any>["columns"] => [
    {
        title: "Receiving time",
        dataIndex: "created_at",
        width: 120,
        ellipsis: true,
    },
    {
        title: "SoC",
        dataIndex: "soc",
        width: 60,
        ellipsis: true,
    },
    {
        title: "Voltage",
        dataIndex: "voltage",
        width: 80,
        ellipsis: true,
    },
    {
        title: "Current",
        dataIndex: "current",
        width: 80,
        ellipsis: true,
    },
    {
        title: "Temperature",
        dataIndex: "temperature",
        width: 80,
        ellipsis: true,
    },
    {
        title: "Cycles",
        dataIndex: "cycles",
        width: 70,
        ellipsis: true,
    },
];

// Desktop columns - show all data
const getDesktopColumns = (): TableProps<any>["columns"] => [
    {
        title: "Receiving time",
        dataIndex: "created_at",
        width: 190,
        ellipsis: true,
    },
    {
        title: "Charging counter",
        dataIndex: "charging_counter",
        width: 150,
        ellipsis: true,
    },
    {
        title: "Voltage",
        dataIndex: "voltage",
        width: 130,
        ellipsis: true,
    },
    {
        title: "Current",
        dataIndex: "current",
        width: 130,
        ellipsis: true,
    },
    {
        title: "SoC",
        dataIndex: "soc",
        width: 130,
        ellipsis: true,
    },
    {
        title: "Temperature",
        dataIndex: "temperature",
        width: 130,
        ellipsis: true,
    },
    {
        title: "Cycles",
        dataIndex: "cycles",
        width: 130,
        ellipsis: true,
    },
    {
        title: "Heartbeat",
        dataIndex: "heartbeat",
        width: 170,
        ellipsis: true,
    },
    {
        title: "Mosfet Charging",
        dataIndex: "mosfet_charging",
        width: 150,
        ellipsis: true,
    },
    {
        title: "Mosfet Discharging",
        dataIndex: "mosfet_discharging",
        width: 160,
        ellipsis: true,
    },
    {
        title: "Individual cell voltages",
        dataIndex: "individual_cell_voltages",
        width: 179,
        ellipsis: true,
        align: "left",
    },
];

// Simplified battery details columns for mobile
const getBatteryColumns = (isMobile: boolean): TableProps<any>["columns"] => [
    {
        title: "",
        dataIndex: "label",
        width: isMobile ? 100 : 180,
        ellipsis: true,
        className: "battery-detail-label",
    },
    {
        title: "",
        dataIndex: "value",
        ellipsis: true,
        className: "battery-detail-value",
        render: (value: string, row: KeyValue, index: number) => {
            if (row.label) {
                return <span className="battery-detail-text">{value}</span>;
            }
            return ValueCell(value, row, index);
        },
    },
];

function ValueCell(value: string, row: KeyValue, index: number) {
    switch (row.type) {
        case "color":
            return (
                <span
                    className="value-with-color"
                    style={
                        {
                            "--color-cell": value,
                        } as React.CSSProperties
                    }
                >
          {value}
        </span>
            );
        default:
            return value;
    }
}

type BatteryDetailParams = {
    batteryId?: string;
};

export default function BatteryDetailTableView() {
    const dispatch = useDispatch();
    const {
        batteryDetail,
        selectedBattery,
        batteryDetailPage,
    } = useSelector<AppState, AppState["batteries"]>((state) => state.batteries);
    const { batteryId } = useParams<BatteryDetailParams>();
    const history = useHistory();
    const [screenSize, setScreenSize] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

    // Handle screen size changes
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            if (width <= 480) {
                setScreenSize('mobile');
            } else if (width <= 768) {
                setScreenSize('tablet');
            } else {
                setScreenSize('desktop');
            }
        };
        
        handleResize(); // Set initial size
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const onClickBack = useCallback(() => {
        history.goBack();
    }, [history]);

    const { selectedPage, totalPages } = batteryDetailPage;

    const onBack = useCallback(() => {
        if (selectedPage > 1) {
            dispatch(setBatteryHBSelectedPage(selectedPage - 1));
        }
    }, [dispatch, selectedPage]);

    const onNext = useCallback(() => {
        if (selectedPage < totalPages) {
            dispatch(setBatteryHBSelectedPage(selectedPage + 1));
        }
    }, [dispatch, selectedPage, totalPages]);

    const resetCallback = useCallback(() => {
        dispatch(setBatteryHBSelectedPage(1));
    }, [dispatch]);

    const batteryData = [
        { label: "ID:", value: selectedBattery?.batteryId || "" },
        { label: "Status:", value: BATTERY_STATUS[selectedBattery?.status || 1 ] },
        { label: "Host ID:", value: selectedBattery?.hostId}
    ];

    const batteryHeartbeatData = useMemo(
        () => parseBatteryDetailTable(batteryDetail),
        [batteryDetail]
    );

    // Get appropriate columns based on screen size
    const getColumns = () => {
        switch (screenSize) {
            case 'mobile':
                return getMobileColumns();
            case 'tablet':
                return getTabletColumns();
            default:
                return getDesktopColumns();
        }
    };

    // Get scroll configuration
    const getScrollConfig = () => {
        switch (screenSize) {
            case 'mobile':
                return { x: 320, y: "60vh" };
            case 'tablet':
                return { x: 600, y: "65vh" };
            default:
                return { x: 'auto', y: "70vh" };
        }
    };

    useEffect(() => {
        if (!selectedBattery) {
            dispatch(getBattery({id: batteryId || ""}));
        }
        if (selectedBattery) {
            dispatch(getBatteryHeartbeats(selectedBattery?.batteryAlias || ""));
        }
    }, [dispatch, selectedBattery, batteryId]);

    return (
        <section className="battery-detail-container">
            <div className="battery-detail-header">
                <Breadcrumb className="battery-breadcrumb" separator={breadcrumbIcon}>
                    <Breadcrumb.Item>
                        <Button type="link" className="batteries-link" onClick={onClickBack}>
                            Battery
                        </Button>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>Details</Breadcrumb.Item>
                </Breadcrumb>
            </div>

            <div className="battery-detail-content">
                <Card
                    className="battery-info-card"
                    title={screenSize === 'mobile' ? "Battery Info" : "Battery Details"} 
                    bordered={false}
                >
                    <Table
                        bordered={false}
                        showHeader={false}
                        customClasses="simple-two-columns"
                        columns={getBatteryColumns(screenSize === 'mobile')}
                        dataSource={batteryData}
                        pagination={false}
                        size={screenSize === 'mobile' ? 'small' : 'middle'}
                    />
                </Card>

                <div className="battery-data-section">
                    <Card className="battery-data-card" bordered={false}>
                        <div className="table-wrapper">
                            <Table
                                columns={getColumns()}
                                dataSource={batteryHeartbeatData}
                             //   scroll={getScrollConfig()}
                                pagination={false}
                                size={screenSize === 'mobile' ? 'small' : 'middle'}
                            />
                        </div>
                        <TablePagination
                            page={batteryDetailPage}
                            onBack={onBack}
                            onNext={onNext}
                            resetCallback={resetCallback}
                        />
                    </Card>
                </div>
            </div>
        </section>
    );
}